// FacturePage.js

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Tooltip } from '@mui/material';
import {
  Container,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField
} from '@mui/material';

import apiUrl from '../config';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { Box } from '@mui/material';
import '../CSS/Facture.css';
import LayoutDashboard from '../layout/LayoutDashboard';
import { DataGrid } from '@mui/x-data-grid';

// ✅ Helper pour formater les montants en € (FR)
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';

  const num = Number(val);

  if (Number.isNaN(num)) return String(val);

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(num);
};


// ✅ Convertit une date affichée en vraie valeur triable
// Accepte : JJ/MM/AAAA, AAAA-MM-JJ, ISO date, vide
const parseDateForSort = (value) => {
  if (!value) return 0;

  const str = value.toString().trim();

  if (!str || str === "—") return 0;

  // Format français : JJ/MM/AAAA
  if (str.includes("/")) {
    const parts = str.split("/");

    if (parts.length === 3) {
      const [day, month, year] = parts;
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);

      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    }
  }

  // Format HTML / MySQL : AAAA-MM-JJ
  if (str.includes("-")) {
    const parsed = new Date(`${str.split("T")[0]}T00:00:00`);

    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  const parsed = new Date(str);

  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const dateSortComparator = (v1, v2) => {
  return parseDateForSort(v1) - parseDateForSort(v2);
};

const FacturePage = () => {
  const [factures, setFactures] = useState([]);
  const [filteredFactures, setFilteredFactures] = useState([]);
  const [loading, setLoading] = useState(true);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [factureToDelete, setFactureToDelete] = useState(null);

  const [filterStatus, setFilterStatus] = useState('Toutes');
  const [clientNames, setClientNames] = useState([]);
  const [filterClient, setFilterClient] = useState('Tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [openUnpayDialog, setOpenUnpayDialog] = useState(false);
  const [factureToToggle, setFactureToToggle] = useState(null);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [nomClient, setNomClient] = useState('');
  const [collaborateur, setCollaborateur] = useState('');
  const [dateFacture, setDateFacture] = useState('');
  const [dateEcheance, setDateEcheance] = useState('');
  const [numeroFacture, setNumeroFacture] = useState('');
  const [montantHT, setMontantHT] = useState('');
  const [montantTTC, setMontantTTC] = useState('');
  const [statusFacture, setStatusFacture] = useState('Non payée');
  const [datePaiement, setDatePaiement] = useState('');
  const [fileFacture, setFileFacture] = useState(null);
  const [loadingAddFacture, setLoadingAddFacture] = useState(false);

  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [datePaiementToggle, setDatePaiementToggle] = useState('');

const [sortModel, setSortModel] = useState([
  { field: 'date_facture', sort: 'desc' }
]);

  // ✅ Modification facture
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [factureToEdit, setFactureToEdit] = useState(null);
  const [editNomClient, setEditNomClient] = useState('');
  const [editCollaborateur, setEditCollaborateur] = useState('');
  const [editDateFacture, setEditDateFacture] = useState('');
  const [editDateEcheance, setEditDateEcheance] = useState('');
  const [editNumeroFacture, setEditNumeroFacture] = useState('');
  const [editMontantHT, setEditMontantHT] = useState('');
  const [editMontantTTC, setEditMontantTTC] = useState('');
  const [editStatusFacture, setEditStatusFacture] = useState('Non payée');
  const [editDatePaiement, setEditDatePaiement] = useState('');
  const [loadingEditFacture, setLoadingEditFacture] = useState(false);

  const resetFilters = () => {
    setFilterStatus('Toutes');
    setFilterClient('Tous');
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const reloadAndReset = async () => {
    resetFilters();
    await fetchFactures(true);
  };

  const normalizeFull = (str) => {
    if (!str) return '';

    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .sort()
      .join(' ');
  };

  const normalizeSearch = (str) => {
    if (!str) return '';

    return str
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  };

  const parseFrenchDate = (dateStr) => {
    if (!dateStr) return null;

    const parts = dateStr.split('/');

    if (parts.length !== 3) return null;

    const [day, month, year] = parts;

    return new Date(`${year}-${month}-${day}T00:00:00`);
  };

  const formatInputDateToFrench = (value) => {
    if (!value) return "";
    if (value.includes("/")) return value;

    const [year, month, day] = value.split("-");

    if (!year || !month || !day) return value;

    return `${day}/${month}/${year}`;
  };

  const formatFrenchDateToInput = (value) => {
    if (!value) return "";

    if (value.includes("-")) {
      return value.split("T")[0];
    }

    const parts = value.split("/");

    if (parts.length !== 3) return "";

    const [day, month, year] = parts;

    if (!day || !month || !year) return "";

    return `${year}-${month}-${day}`;
  };

  const updateClientNamesFromFactures = (data) => {
    const uniqueClientsMap = new Map();

    data.forEach(f => {
      const original = f.nom_client;
      const normalized = normalizeFull(original);

      if (normalized && !uniqueClientsMap.has(normalized)) {
        uniqueClientsMap.set(normalized, original?.trim() ?? '');
      }
    });

    const uniqueClients = Array.from(uniqueClientsMap.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], 'fr', { sensitivity: 'base' })
    );

    setClientNames(uniqueClients);
  };

  const syncFacturesState = (data) => {
    const safeData = Array.isArray(data) ? data : [];

    setFactures(safeData);
    setFilteredFactures(safeData);
    sessionStorage.removeItem("factures_cache");
    updateClientNamesFromFactures(safeData);
  };

  const isOverdue = (facture) => {
    if (!facture?.date_echeance || facture.status === "Payée") return false;

    const dueDate = parseFrenchDate(facture.date_echeance);

    if (!dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const fetchFactures = async (forceRefresh = false) => {
    sessionStorage.removeItem("factures_cache");

    if (factures.length === 0 || forceRefresh) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(
        `${apiUrl}/facturebiam?fresh=1&t=${Date.now()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache"
          }
        }
      );

      syncFacturesState(response.data || []);

    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);

      setFactures([]);
      setFilteredFactures([]);

      setNotification({
        open: true,
        message: "Erreur lors du chargement des factures.",
        severity: "error"
      });

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactures(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = [...factures];

    if (searchTerm.trim() !== '') {
      const term = normalizeSearch(searchTerm);

      filtered = filtered.filter((f) => {
        const searchableContent = normalizeSearch(`
          ${f.nom_client || ''}
          ${f.numero_facture || ''}
          ${f.collaborateur || ''}
          ${f.status || ''}
          ${f.date_facture || ''}
          ${f.date_echeance || ''}
          ${f.date_paiement || ''}
          ${f.montant_ht || ''}
          ${f.montant_ttc || ''}
        `);

        return searchableContent.includes(term);
      });
    }

    if (filterStatus === 'Payée') {
      filtered = filtered.filter(f => f.status === 'Payée');
    }

    if (filterStatus === 'Non payée') {
      filtered = filtered.filter(f => f.status === 'Non payée');
    }

    if (filterStatus === 'En retard') {
      filtered = filtered.filter(f => isOverdue(f));
    }

    if (filterClient !== 'Tous') {
      filtered = filtered.filter(f => normalizeFull(f.nom_client) === filterClient);
    }

    if (filterDateFrom) {
      const fromDate = new Date(`${filterDateFrom}T00:00:00`);

      filtered = filtered.filter(f => {
        const factureDate = parseFrenchDate(f.date_facture);
        return factureDate && factureDate >= fromDate;
      });
    }

    if (filterDateTo) {
      const toDate = new Date(`${filterDateTo}T23:59:59`);

      filtered = filtered.filter(f => {
        const factureDate = parseFrenchDate(f.date_facture);
        return factureDate && factureDate <= toDate;
      });
    }

    setFilteredFactures(filtered);
  }, [searchTerm, filterStatus, filterClient, filterDateFrom, filterDateTo, factures]);

  const updateStatus = async (factureId, newStatus, paymentDate = null) => {
    if (!factureId) return;

    if (newStatus === "Payée" && !paymentDate) {
      setNotification({
        open: true,
        message: "Veuillez préciser la date de paiement.",
        severity: "error"
      });
      return;
    }

    setIsUpdatingStatus(true);

    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${apiUrl}/facturebiam/status/${factureId}`,
        {
          status: newStatus,
          date_paiement: newStatus === "Payée" ? paymentDate : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedFactures = factures.map(f =>
        f.id_facture === factureId
          ? {
              ...f,
              status: newStatus,
              date_paiement: newStatus === "Payée"
                ? formatInputDateToFrench(paymentDate)
                : ""
            }
          : f
      );

      syncFacturesState(updatedFactures);
      resetFilters();

      await fetchFactures(true);

      setNotification({
        open: true,
        message: newStatus === 'Payée'
          ? 'Facture marquée comme payée.'
          : 'Facture remise en "Non payée".',
        severity: 'success'
      });

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Erreur lors de la mise à jour de la facture.',
        severity: 'error'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const openEditFactureDialog = (facture) => {
    setFactureToEdit(facture);

    setEditNomClient(facture.nom_client || '');
    setEditCollaborateur(facture.collaborateur || '');
    setEditNumeroFacture(facture.numero_facture || '');
    setEditMontantHT(facture.montant_ht || '');
    setEditMontantTTC(facture.montant_ttc || '');
    setEditDateFacture(formatFrenchDateToInput(facture.date_facture));
    setEditDateEcheance(formatFrenchDateToInput(facture.date_echeance));
    setEditStatusFacture(facture.status || 'Non payée');
    setEditDatePaiement(formatFrenchDateToInput(facture.date_paiement));

    setOpenEditDialog(true);
  };

  const handleUpdateFacture = async () => {
    if (!factureToEdit?.id_facture) return;

    if (
      !editNomClient ||
      !editCollaborateur ||
      !editDateFacture ||
      !editDateEcheance ||
      !editMontantHT ||
      !editMontantTTC ||
      (editStatusFacture === "Payée" && !editDatePaiement)
    ) {
      setNotification({
        open: true,
        message: editStatusFacture === "Payée" && !editDatePaiement
          ? "Veuillez préciser la date de paiement."
          : "Veuillez remplir tous les champs obligatoires.",
        severity: "error"
      });
      return;
    }

    setLoadingEditFacture(true);

    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${apiUrl}/facturebiam/${factureToEdit.id_facture}`,
        {
          numero_facture: editNumeroFacture,
          nom_client: editNomClient,
          collaborateur: editCollaborateur,
          montant_ht: editMontantHT,
          montant_ttc: editMontantTTC,
          date_facture: editDateFacture,
          date_echeance: editDateEcheance,
          status: editStatusFacture,
          date_paiement: editStatusFacture === "Payée" ? editDatePaiement : null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const updatedFactures = factures.map((f) => {
        if (f.id_facture !== factureToEdit.id_facture) {
          return f;
        }

        return {
          ...f,
          numero_facture: editNumeroFacture,
          nom_client: editNomClient,
          collaborateur: editCollaborateur,
          montant_ht: editMontantHT,
          montant_ttc: editMontantTTC,
          date_facture: formatInputDateToFrench(editDateFacture),
          date_echeance: formatInputDateToFrench(editDateEcheance),
          status: editStatusFacture,
          date_paiement: editStatusFacture === "Payée"
            ? formatInputDateToFrench(editDatePaiement)
            : ""
        };
      });

      syncFacturesState(updatedFactures);
      resetFilters();

      setNotification({
        open: true,
        message: "Facture modifiée avec succès.",
        severity: "success"
      });

      setOpenEditDialog(false);
      setFactureToEdit(null);

      await fetchFactures(true);

    } catch (error) {
      console.error(error);

      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors de la modification de la facture.",
        severity: "error"
      });

    } finally {
      setLoadingEditFacture(false);
    }
  };

  const handleDeleteFacture = async () => {
    if (!factureToDelete) return;

    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');

      await axios.delete(`${apiUrl}/facturebiam/${factureToDelete.id_facture}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedFactures = factures.filter(
        f => f.id_facture !== factureToDelete.id_facture
      );

      syncFacturesState(updatedFactures);
      resetFilters();

      await fetchFactures(true);

      setNotification({
        open: true,
        message: 'Facture supprimée avec succès.',
        severity: 'success'
      });

    } catch (error) {
      console.error(error);

      setNotification({
        open: true,
        message: 'Erreur lors de la suppression de la facture.',
        severity: 'error'
      });

    } finally {
      setIsDeleting(false);
      setOpenDialog(false);
      setFactureToDelete(null);
    }
  };

  const handleDownload = async (factureId) => {
    const token = localStorage.getItem('token');

    try {
      const response = await axios.get(`${apiUrl}/facturebiam/${factureId}/telecharger`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = urlBlob;
      link.setAttribute('download', `facture_${factureId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

    } catch (error) {
      console.error('Erreur lors du téléchargement de la facture:', error);

      setNotification({
        open: true,
        message: 'Erreur lors du téléchargement de la facture',
        severity: 'error'
      });
    }
  };

  const handleAddFacture = async () => {
    if (
      !nomClient ||
      !dateFacture ||
      !dateEcheance ||
      !fileFacture ||
      !collaborateur ||
      !montantHT ||
      !montantTTC ||
      (statusFacture === "Payée" && !datePaiement)
    ) {
      setNotification({
        open: true,
        message: statusFacture === "Payée" && !datePaiement
          ? "Veuillez préciser la date de paiement."
          : "Veuillez remplir tous les champs.",
        severity: "error"
      });

      return;
    }

    const formData = new FormData();

    formData.append("facture_pdf", fileFacture);
    formData.append("date_facture", dateFacture);
    formData.append("date_echeance", dateEcheance);
    formData.append("status", statusFacture);
    formData.append("date_paiement", statusFacture === "Payée" ? datePaiement : "");
    formData.append("nom_client", nomClient);
    formData.append("collaborateur", collaborateur);
    formData.append("montant_ht", montantHT);
    formData.append("montant_ttc", montantTTC);
    formData.append("numero_facture", numeroFacture);

    setLoadingAddFacture(true);

    try {
      const token = localStorage.getItem('token');

      await axios.post(`${apiUrl}/facturebiam/ajouter`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setNotification({
        open: true,
        message: "Facture ajoutée avec succès.",
        severity: "success"
      });

      setOpenAddDialog(false);

      await reloadAndReset();

      setNomClient('');
      setCollaborateur('');
      setDateFacture('');
      setDateEcheance('');
      setNumeroFacture('');
      setMontantHT('');
      setMontantTTC('');
      setStatusFacture('Non payée');
      setDatePaiement('');
      setFileFacture(null);

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors de l'ajout de la facture.",
        severity: "error"
      });

    } finally {
      setLoadingAddFacture(false);
    }
  };

  const rows = useMemo(() => {
  return [...filteredFactures]
    .sort((a, b) => {
      const diff = parseDateForSort(b.date_facture) - parseDateForSort(a.date_facture);

      if (diff !== 0) return diff;

      return Number(b.id_facture || 0) - Number(a.id_facture || 0);
    })
    .map((f) => ({
      id: f.id_facture,
      ...f
    }));
}, [filteredFactures]);

  const totalFactures = factures.length;
  const totalPayees = factures.filter(f => f.status === "Payée").length;
  const totalNonPayees = factures.filter(f => f.status === "Non payée").length;
  const totalEnRetard = factures.filter(f => isOverdue(f)).length;

  const totalTTC = factures.reduce((sum, f) => {
    const value = Number(f.montant_ttc || 0);
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);

  const columns = [
{
      field: "nom_client",
      headerName: "Client",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
{
      field: "numero_facture",
      headerName: "N° Facture",
      flex: 1,
      minWidth: 170
    },
{
      field: "statusAction",
      headerName: "Paiement",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <CheckCircleIcon
          sx={{ cursor: "pointer" }}
          color={params.row.status === "Payée" ? "success" : "action"}
          onClick={() => {
            if (params.row.status === "Payée") {
              setFactureToToggle(params.row);
              setOpenUnpayDialog(true);
            } else {
              setFactureToToggle(params.row);
              setDatePaiementToggle("");
              setOpenPayDialog(true);
            }
          }}
        />
      )
    },
{
      field: "date_facture",
      headerName: "Date facture",
      flex: 1,
      minWidth: 170,
      sortComparator: dateSortComparator,
    },
{
      field: "date_echeance",
      headerName: "Date échéance",
      flex: 1,
      minWidth: 170,
      sortComparator: dateSortComparator,
    },
{
      field: "status",
      headerName: "Statut",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const overdue = isOverdue(params.row);

        return (
          <span
            className={
              params.value === "Payée"
                ? "status-badge status-paid"
                : overdue
                  ? "status-badge status-late"
                  : "status-badge status-unpaid"
            }
          >
            {overdue ? "En retard" : params.value}
          </span>
        );
      }
    },
{
      field: "date_paiement",
      headerName: "Date paiement",
      flex: 1,
      minWidth: 170,
      sortComparator: dateSortComparator,
      renderCell: (params) => (
        <span>{params.value || "—"}</span>
      )
    },
{
      field: "collaborateur",
      headerName: "Collaborateur",
      flex: 1,
      minWidth: 220
    },
{
      field: "montant_ht",
      headerName: "Montant HT",
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => formatCurrency(params.value)
    },
{
      field: "montant_ttc",
      headerName: "Montant TTC",
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => formatCurrency(params.value)
    },
{
      field: "edit",
      headerName: "Modifier",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <EditIcon
          className="facture-edit-icon"
          sx={{ cursor: "pointer" }}
          onClick={() => openEditFactureDialog(params.row)}
        />
      )
    },
{
      field: "download",
      headerName: "Télécharger",
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <DownloadIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleDownload(params.row.id_facture)}
        />
      )
    },
{
      field: "delete",
      headerName: "Supprimer",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <DeleteIcon
          color="error"
          sx={{ cursor: "pointer" }}
          onClick={() => {
            setFactureToDelete(params.row);
            setOpenDialog(true);
          }}
        />
      )
    }
];

  return (
    <LayoutDashboard>

      <div className="dashboard-page">

        <div className="welcome-section">
          <h2>Gestion des Factures</h2>
          <p>
            Consultez, modifiez, téléchargez et gérez les factures des clients.
          </p>
        </div>

        <Container maxWidth="lg">

          <Box className="facture-topbar">

            <Box>
              <Typography className="facture-eyebrow">
                Espace facturation
              </Typography>

              <Typography className="facture-subtitle">
                Suivi des factures, paiements, échéances et documents PDF.
              </Typography>
            </Box>

            <Button
              variant="contained"
              className="facture-primary-btn"
              onClick={() => setOpenAddDialog(true)}
            >
              + Ajouter une facture
            </Button>

          </Box>

          <Box className="facture-kpi-grid">

            <Box className="facture-kpi-card">
              <span className="facture-kpi-label">Total factures</span>
              <strong>{totalFactures}</strong>
            </Box>

            <Box className="facture-kpi-card">
              <span className="facture-kpi-label">Payées</span>
              <strong className="kpi-green">{totalPayees}</strong>
            </Box>

            <Box className="facture-kpi-card">
              <span className="facture-kpi-label">Non payées</span>
              <strong className="kpi-red">{totalNonPayees}</strong>
            </Box>

            <Box className="facture-kpi-card">
              <span className="facture-kpi-label">En retard</span>
              <strong className="kpi-orange">{totalEnRetard}</strong>
            </Box>

            <Box className="facture-kpi-card">
              <span className="facture-kpi-label">Total TTC</span>
              <strong>{formatCurrency(totalTTC)}</strong>
            </Box>

          </Box>

          <Box className="facture-filter-card">

            <Box className="facture-filter-header">
              <Box>
                <Typography className="facture-filter-title">
                  Filtres de recherche
                </Typography>

                <Typography className="facture-filter-description">
                  Recherchez par client, numéro de facture, collaborateur, statut ou période.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                className="facture-reset-btn"
                onClick={resetFilters}
              >
                Réinitialiser
              </Button>
            </Box>

            <Box className="facture-filter-grid">

              <Box className="facture-filter-item facture-filter-search">
                <Typography className="facture-filter-label">
                  Recherche globale
                </Typography>

                <TextField
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Client, facture, collaborateur..."
                  fullWidth
                  size="small"
                />
              </Box>

              <Box className="facture-filter-item">
                <Typography className="facture-filter-label">
                  Statut
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="Toutes">Toutes les factures</MenuItem>
                    <MenuItem value="Payée">Payées</MenuItem>
                    <MenuItem value="Non payée">Non payées</MenuItem>
                    <MenuItem value="En retard">En retard</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box className="facture-filter-item">
                <Typography className="facture-filter-label">
                  Client
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={filterClient}
                    onChange={(e) => setFilterClient(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="Tous">Tous les clients</MenuItem>
                    {clientNames.map(([key, original], index) => (
                      <MenuItem key={index} value={key}>
                        {original}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box className="facture-filter-item">
                <Typography className="facture-filter-label">
                  Date facture de
                </Typography>

                <TextField
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box className="facture-filter-item">
                <Typography className="facture-filter-label">
                  Jusqu’à
                </Typography>

                <TextField
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>

            </Box>

            <Box className="facture-result-line">
              {filteredFactures.length} facture(s) affichée(s) sur {factures.length}
            </Box>

          </Box>

          <div className="facture-table-wrapper">
            <Box sx={{ width: "100%", mt: 3 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading && rows.length === 0}
                autoHeight
                pageSizeOptions={[5, 10, 20, 50]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 }
                  }
                }}
                disableRowSelectionOnClick
                sortModel={sortModel}
                onSortModelChange={(newModel) => {
                  setSortModel(newModel.length ? newModel : [{ field: 'date_facture', sort: 'desc' }]);
                }}
                sx={{
                  width: "100%",
                  borderRadius: 3,
                  background: "white",

                  "& .MuiDataGrid-columnHeaders": {
                    background: "#eef2f7",
                    fontWeight: "700",
                    fontSize: "15px",
                    color: "#1f6391",
                    borderBottom: "2px solid #e5e7eb"
                  },

                  "& .MuiDataGrid-cell": {
                    fontSize: "14px",
                    color: "#374151"
                  },

                  "& .MuiDataGrid-footerContainer": {
                    justifyContent: "space-between",
                    paddingLeft: "16px",
                    paddingRight: "16px"
                  },

                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "normal"
                  }
                }}
              />
            </Box>
          </div>

        </Container>

        {/* Dialog modifier */}
        <Dialog
          open={openEditDialog}
          onClose={() => !loadingEditFacture && setOpenEditDialog(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              border: "none",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              borderRadius: "14px"
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: "bold", color: "#1f6391", textAlign: "center" }}>
            Modifier la facture
          </DialogTitle>

          <DialogContent
            sx={{
              padding: 3,
              background: "transparent",
              boxShadow: "none",
              border: "none"
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Numéro de facture
              </Typography>

              <TextField
                variant="outlined"
                value={editNumeroFacture}
                onChange={(e) => setEditNumeroFacture(e.target.value)}
                fullWidth
                size="small"
              />

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Nom du client
              </Typography>

              <TextField
                variant="outlined"
                value={editNomClient}
                onChange={(e) => setEditNomClient(e.target.value)}
                fullWidth
                size="small"
              />

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Collaborateur
              </Typography>

              <TextField
                variant="outlined"
                value={editCollaborateur}
                onChange={(e) => setEditCollaborateur(e.target.value)}
                fullWidth
                size="small"
              />

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Montant HT
              </Typography>

              <TextField
                variant="outlined"
                type="number"
                value={editMontantHT}
                onChange={(e) => setEditMontantHT(e.target.value)}
                fullWidth
                size="small"
              />

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Montant TTC
              </Typography>

              <TextField
                variant="outlined"
                type="number"
                value={editMontantTTC}
                onChange={(e) => setEditMontantTTC(e.target.value)}
                fullWidth
                size="small"
              />

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Date facture
              </Typography>

              <TextField
                variant="outlined"
                type="date"
                value={editDateFacture}
                onChange={(e) => setEditDateFacture(e.target.value)}
                fullWidth
                size="small"
              />

              <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                Date échéance
              </Typography>

              <TextField
                variant="outlined"
                type="date"
                value={editDateEcheance}
                onChange={(e) => setEditDateEcheance(e.target.value)}
                fullWidth
                size="small"
              />

              <Box>
                <Typography sx={{ fontWeight: 600, color: "#1f6391" }}>
                  Statut
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={editStatusFacture}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditStatusFacture(value);

                      if (value === "Non payée") {
                        setEditDatePaiement("");
                      }
                    }}
                  >
                    <MenuItem value="Non payée">Non payée</MenuItem>
                    <MenuItem value="Payée">Payée</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {editStatusFacture === "Payée" && (
                <>
                  <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                    Date de paiement
                  </Typography>

                  <TextField
                    variant="outlined"
                    type="date"
                    value={editDatePaiement}
                    onChange={(e) => setEditDatePaiement(e.target.value)}
                    fullWidth
                    size="small"
                    required
                  />
                </>
              )}

              <Typography sx={{ color: "#64748b", fontSize: "14px", mt: 1 }}>
                Le PDF existant est conservé. Cette modification change uniquement les informations de la facture.
              </Typography>

            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenEditDialog(false)}
              sx={{ borderColor: "#1f6391", color: "#1f6391" }}
              disabled={loadingEditFacture}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={handleUpdateFacture}
              sx={{ bgcolor: "#1f6391" }}
              disabled={loadingEditFacture}
            >
              {loadingEditFacture ? "Modification..." : "Enregistrer les modifications"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog supprimer */}
        <Dialog open={openDialog} onClose={() => !isDeleting && setOpenDialog(false)}>
          <DialogTitle>Êtes-vous sûr ?</DialogTitle>

          <DialogContent sx={{ px: 4, py: 2 }}>
            <Typography>
              {isDeleting
                ? "Suppression en cours, veuillez patienter..."
                : "Voulez-vous vraiment supprimer cette facture ?"}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenDialog(false)}
              color="primary"
              disabled={isDeleting}
            >
              Annuler
            </Button>

            <Button
              onClick={handleDeleteFacture}
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog marquer comme Payée */}
        <Dialog open={openPayDialog} onClose={() => !isUpdatingStatus && setOpenPayDialog(false)}>
          <DialogTitle>Marquer comme payée</DialogTitle>

          <DialogContent sx={{ px: 4, py: 2 }}>
            <Typography sx={{ mb: 2 }}>
              Veuillez préciser la date de paiement.
            </Typography>

            <TextField
              type="date"
              value={datePaiementToggle}
              onChange={(e) => setDatePaiementToggle(e.target.value)}
              fullWidth
              size="small"
              required
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenPayDialog(false)}
              color="primary"
              disabled={isUpdatingStatus}
            >
              Annuler
            </Button>

            <Button
              onClick={async () => {
                if (!datePaiementToggle) {
                  setNotification({
                    open: true,
                    message: "Veuillez préciser la date de paiement.",
                    severity: "error"
                  });
                  return;
                }

                if (factureToToggle && !isUpdatingStatus) {
                  await updateStatus(factureToToggle.id_facture, "Payée", datePaiementToggle);
                  setOpenPayDialog(false);
                  setFactureToToggle(null);
                  setDatePaiementToggle("");
                }
              }}
              color="primary"
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? "Mise à jour..." : "Confirmer"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog remettre en Non payée */}
        <Dialog open={openUnpayDialog} onClose={() => !isUpdatingStatus && setOpenUnpayDialog(false)}>
          <DialogTitle>Confirmer</DialogTitle>

          <DialogContent sx={{ px: 4, py: 2 }}>
            <Typography>
              {isUpdatingStatus
                ? "Mise à jour en cours, veuillez patienter..."
                : 'Remettre cette facture en "Non payée" ?'}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenUnpayDialog(false)}
              color="primary"
              disabled={isUpdatingStatus}
            >
              Annuler
            </Button>

            <Button
              onClick={async () => {
                if (factureToToggle && !isUpdatingStatus) {
                  await updateStatus(factureToToggle.id_facture, 'Non payée');
                  setOpenUnpayDialog(false);
                  setFactureToToggle(null);
                }
              }}
              color="error"
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? "Mise à jour..." : "Confirmer"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={3000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{
              width: '100%',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

      </div>

      {/* Dialog ajouter */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            border: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: "14px"
          }
        }}
      >

        <DialogTitle sx={{ fontWeight: "bold", color: "#1f6391", textAlign: "center" }}>
          Ajouter une facture
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 3,
            background: "transparent",
            boxShadow: "none",
            border: "none"
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Numéro de facture
            </Typography>

            <TextField
              variant="outlined"
              value={numeroFacture}
              onChange={(e) => setNumeroFacture(e.target.value)}
              fullWidth
              size="small"
            />

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Nom du client
            </Typography>

            <TextField
              variant="outlined"
              value={nomClient}
              onChange={(e) => setNomClient(e.target.value)}
              fullWidth
              size="small"
            />

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Collaborateur
            </Typography>

            <TextField
              variant="outlined"
              value={collaborateur}
              onChange={(e) => setCollaborateur(e.target.value)}
              fullWidth
              size="small"
            />

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Montant HT
            </Typography>

            <TextField
              variant="outlined"
              type="number"
              value={montantHT}
              onChange={(e) => setMontantHT(e.target.value)}
              fullWidth
              size="small"
            />

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Montant TTC
            </Typography>

            <TextField
              variant="outlined"
              type="number"
              value={montantTTC}
              onChange={(e) => setMontantTTC(e.target.value)}
              fullWidth
              size="small"
            />

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Date facture
            </Typography>

            <TextField
              variant="outlined"
              type="date"
              value={dateFacture}
              onChange={(e) => setDateFacture(e.target.value)}
              fullWidth
              size="small"
            />

            <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
              Date échéance
            </Typography>

            <TextField
              variant="outlined"
              type="date"
              value={dateEcheance}
              onChange={(e) => setDateEcheance(e.target.value)}
              fullWidth
              size="small"
            />

            <Box>
              <Typography sx={{ fontWeight: 600, color: "#1f6391" }}>
                Statut
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={statusFacture}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStatusFacture(value);

                    if (value === "Non payée") {
                      setDatePaiement("");
                    }
                  }}
                >
                  <MenuItem value="Non payée">Non payée</MenuItem>
                  <MenuItem value="Payée">Payée</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {statusFacture === "Payée" && (
              <>
                <Typography sx={{ fontWeight: 600, color: "#1f6391", mb: 0.5 }}>
                  Date de paiement
                </Typography>

                <TextField
                  variant="outlined"
                  type="date"
                  value={datePaiement}
                  onChange={(e) => setDatePaiement(e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </>
            )}

            <Button
              variant="outlined"
              component="label"
              sx={{
                borderColor: "#1f6391",
                color: "#1f6391",
                justifyContent: "flex-start",
                textTransform: "none"
              }}
            >
              {fileFacture ? fileFacture.name : "Choisir fichier PDF"}

              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => setFileFacture(e.target.files[0])}
              />
            </Button>

          </Box>

        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>

          <Button
            variant="outlined"
            onClick={() => setOpenAddDialog(false)}
            sx={{ borderColor: "#1f6391", color: "#1f6391" }}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            onClick={handleAddFacture}
            sx={{ bgcolor: "#1f6391" }}
            disabled={loadingAddFacture}
          >
            {loadingAddFacture ? "Merci de patienter..." : "Ajouter la facture"}
          </Button>

        </DialogActions>

      </Dialog>

    </LayoutDashboard>
  );
};

export default FacturePage;